import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useRouteBreadcrumbs } from './useRouteBreadcrumbs';

interface AppBreadcrumbSlot {
  /**
   * True when the active route defers its breadcrumb to the page (see
   * `RouteBreadcrumbs`) *and* the sidenav layout is mounted to receive it. A
   * page reads this to decide whether to render its own inline breadcrumb or
   * hand the trail to `DeferredBreadcrumb`. It is known during render, so a page
   * never paints its inline crumbs for a frame before hiding them.
   */
  isDeferred: boolean;
  /**
   * True when the `AppBreadcrumb` bar is mounted. Unlike `isDeferred` it says
   * nothing about who owns the trail, only that a bar exists to render one — a
   * page with a route-declared trail reads it to drop its inline copy.
   */
  isLifted: boolean;
  /** The `AppBreadcrumb` bar element the deferred trail portals into. */
  node: HTMLElement | null;
  setNode: (node: HTMLElement | null) => void;
}

// Outside the sidenav layout (legacy navbar, or any page rendered standalone in
// a test) nothing defers and there is no slot, so pages keep their inline
// breadcrumbs and `DeferredBreadcrumb` renders nothing.
const AppBreadcrumbSlotContext = createContext<AppBreadcrumbSlot>({
  isDeferred: false,
  isLifted: false,
  node: null,
  setNode: () => undefined,
});

/**
 * Owns the single breadcrumb slot in the sidenav layout: `AppBreadcrumb`
 * registers the element, and a deferred page portals its trail into it. Wraps
 * both so the bar and the routed content — siblings in `LoggedInLayout` — can
 * meet without threading props through the page tree.
 *
 * Mount it only inside the sidenav layout: `isDeferred` is derived from the
 * route alone, so a provider mounted under the legacy navbar would tell pages to
 * defer to a bar that isn't there.
 */
export function AppBreadcrumbSlotProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const isDeferred = useRouteBreadcrumbs().mode === 'deferred';
  // Mounted only by the sidenav layout, so presence alone is `isLifted`.
  const value = useMemo(
    () => ({ isDeferred, isLifted: true, node, setNode }),
    [isDeferred, node],
  );

  return (
    <AppBreadcrumbSlotContext.Provider value={value}>
      {children}
    </AppBreadcrumbSlotContext.Provider>
  );
}

export function useAppBreadcrumbSlot(): AppBreadcrumbSlot {
  return useContext(AppBreadcrumbSlotContext);
}
